/**
 * The edges of a Type 2 charstring's stem hints, the unit that blue zones capture and the hint map
 * aligns to the pixel grid. A stem is a pair of edges, or a ghost: one edge declared by a stem of
 * width -20 or -21.
 *
 * Portions ported from FreeType (FreeType Project License).
 * Copyright 2007-2014 Adobe Systems Incorporated.
 * See THIRD-PARTY-NOTICES.md.
 */
import { doubleToFixed, intToFixed } from './cffFixed';
import { ftMulFix } from './fontMetrics';
/** `CF2_HintRec` flags. */
export const EdgeFlag = {
    GhostBottom: 0x1,
    GhostTop: 0x2,
    PairBottom: 0x4,
    PairTop: 0x8,
    Locked: 0x10,
    Synthetic: 0x20,
};
/** `CF2_MIN_COUNTER`: the least space, in pixels, between two hint edges. */
export const MIN_COUNTER = doubleToFixed(0.5);
/** The ghost-bottom width, and the ghost-top width. A stem of either has one edge. */
const GHOST_BOTTOM_WIDTH = intToFixed(-21);
const GHOST_TOP_WIDTH = intToFixed(-20);
const has = (edge, flags) => (edge.flags & flags) !== 0;
export const isValidEdge = (edge) => edge.flags !== 0;
export const isPairEdge = (edge) => has(edge, EdgeFlag.PairBottom | EdgeFlag.PairTop);
export const isPairTopEdge = (edge) => has(edge, EdgeFlag.PairTop);
export const isTopEdge = (edge) => has(edge, EdgeFlag.PairTop | EdgeFlag.GhostTop);
export const isBottomEdge = (edge) => has(edge, EdgeFlag.PairBottom | EdgeFlag.GhostBottom);
export const isLockedEdge = (edge) => has(edge, EdgeFlag.Locked);
export const isSyntheticEdge = (edge) => has(edge, EdgeFlag.Synthetic);
export function noEdge() {
    return { flags: 0, csCoord: 0, dsCoord: 0, scale: 0, index: 0 };
}
/**
 * The bottom or the top edge of `stem` (`cf2_hint_init`), or no edge when a ghost stem has none
 * there. A stem used before keeps its place and is locked. `hintOrigin` is where a `seac` accent
 * starts, which moves its stems.
 */
export function stemEdge(stem, index, isBottom, hintOrigin, scale) {
    const width = stem.max - stem.min;
    let csCoord = 0;
    let flags = 0;
    if (width === GHOST_BOTTOM_WIDTH) {
        if (isBottom) {
            csCoord = stem.max;
            flags = EdgeFlag.GhostBottom;
        }
    }
    else if (width === GHOST_TOP_WIDTH) {
        if (!isBottom) {
            csCoord = stem.min;
            flags = EdgeFlag.GhostTop;
        }
    }
    else {
        // An inverted pair swaps its edges: CoolType does, and so does FreeType.
        const [low, high] = width < 0 ? [stem.max, stem.min] : [stem.min, stem.max];
        csCoord = isBottom ? low : high;
        flags = isBottom ? EdgeFlag.PairBottom : EdgeFlag.PairTop;
    }
    csCoord += hintOrigin;
    const edge = { flags, csCoord, dsCoord: ftMulFix(csCoord, scale), scale, index };
    if (flags === 0 || !stem.used)
        return edge;
    edge.dsCoord = isTopEdge(edge) ? stem.maxDS : stem.minDS;
    edge.flags |= EdgeFlag.Locked;
    return edge;
}
//# sourceMappingURL=cffHintEdge.js.map